export interface CalculateWeightForLengthParams {
  length: number; // Length in cm (45-110 cm for 0-2 years)
  weight: number; // Weight in kg
  sex: 'male' | 'female';
  lengthUnit?: 'cm' | 'in';
  weightUnit?: 'kg' | 'lbs';
}

export interface WeightForLengthResult {
  percentile: number;
  zScore: number;
  category: 'Severely wasted' | 'Wasted' | 'Normal' | 'Possible risk of overweight' | 'Overweight' | 'Obese';
  categoryDescription: string;
  interpretation: string;
}

// WHO Weight-for-Length LMS parameters (subset for common lengths)
// Full dataset would include every 0.1cm from 45-110cm
const LMS_DATA: {
  [key in 'male' | 'female']: {
    [length: number]: {L: number; M: number; S: number};
  };
} = {
  male: {
    45.0: {L: 0.3487, M: 2.441, S: 0.09182},
    50.0: {L: 0.3809, M: 3.346, S: 0.08738},
    55.0: {L: 0.4094, M: 4.315, S: 0.08301},
    60.0: {L: 0.4344, M: 5.318, S: 0.07895},
    65.0: {L: 0.456, M: 6.329, S: 0.07531},
    70.0: {L: 0.4744, M: 7.328, S: 0.07211},
    75.0: {L: 0.4897, M: 8.302, S: 0.06934},
    80.0: {L: 0.5021, M: 9.245, S: 0.06698},
    85.0: {L: 0.5117, M: 10.154, S: 0.06499},
    90.0: {L: 0.5188, M: 11.03, S: 0.06332},
    95.0: {L: 0.5234, M: 11.875, S: 0.06193},
    100.0: {L: 0.5256, M: 12.693, S: 0.06077},
    105.0: {L: 0.5254, M: 13.488, S: 0.05981},
    110.0: {L: 0.5227, M: 14.265, S: 0.05902},
  },
  female: {
    45.0: {L: 0.3809, M: 2.369, S: 0.08949},
    50.0: {L: 0.4142, M: 3.231, S: 0.08528},
    55.0: {L: 0.4435, M: 4.149, S: 0.08124},
    60.0: {L: 0.4689, M: 5.096, S: 0.07751},
    65.0: {L: 0.4905, M: 6.052, S: 0.0742},
    70.0: {L: 0.5085, M: 7.0, S: 0.07131},
    75.0: {L: 0.5229, M: 7.927, S: 0.06884},
    80.0: {L: 0.5339, M: 8.83, S: 0.06675},
    85.0: {L: 0.5416, M: 9.708, S: 0.06499},
    90.0: {L: 0.546, M: 10.563, S: 0.06353},
    95.0: {L: 0.5472, M: 11.396, S: 0.06232},
    100.0: {L: 0.5452, M: 12.21, S: 0.06131},
    105.0: {L: 0.5401, M: 13.009, S: 0.06048},
    110.0: {L: 0.5318, M: 13.798, S: 0.05979},
  },
};

/**
 * Calculates Weight-for-Length percentile for infants and toddlers (0-2 years) using WHO growth standards
 * Based on the LMS (Lambda-Mu-Sigma) method
 *
 * Weight-for-length is used to assess nutritional status and growth in young children
 * who may not be able to stand for height measurement
 *
 * @param params - Object containing length, weight, and sex
 * @returns WeightForLengthResult with percentile, z-score, and category
 *
 * @example
 * calculateWeightForLength({
 *   length: 75,
 *   weight: 9.5,
 *   sex: 'male'
 * })
 */
export const calculateWeightForLengthPercentile = (params: CalculateWeightForLengthParams): WeightForLengthResult => {
  const {length, weight, sex, lengthUnit = 'cm', weightUnit = 'kg'} = params;

  // Convert length to cm
  const lengthInCm = lengthUnit === 'cm' ? length : length * 2.54;

  // Convert weight to kg
  const weightInKg = weightUnit === 'kg' ? weight : weight * 0.453592;

  // Validate inputs
  if (lengthInCm < 45 || lengthInCm > 110) {
    throw new Error('Length must be between 45 and 110 cm (approximately 0-2 years)');
  }
  if (weightInKg <= 0 || weightInKg > 30) {
    throw new Error('Weight must be between 0 and 30 kg');
  }

  // Get LMS parameters for the given length and sex
  const lmsParams = getLMSParameters(lengthInCm, sex);
  const {L, M, S} = lmsParams;

  // Calculate Z-score using the LMS method
  // Z = [(Weight/M)^L - 1] / (L × S)
  const zScore = L !== 0 ? (Math.pow(weightInKg / M, L) - 1) / (L * S) : Math.log(weightInKg / M) / S; // Special case when L = 0

  // Convert Z-score to percentile
  const percentile = zScoreToPercentile(zScore);

  // Determine category based on WHO standards
  const {category, categoryDescription, interpretation} = getCategory(zScore, percentile);

  return {
    percentile: Math.round(percentile * 10) / 10,
    zScore: Math.round(zScore * 100) / 100,
    category,
    categoryDescription,
    interpretation,
  };
};

/**
 * Gets LMS parameters for a given length and sex, with linear interpolation for values between data points
 */
function getLMSParameters(length: number, sex: 'male' | 'female'): {L: number; M: number; S: number} {
  const sexData = LMS_DATA[sex];
  const lengths = Object.keys(sexData)
    .map(Number)
    .sort((a, b) => a - b);

  // Check for exact match
  if (sexData[length]) {
    return sexData[length];
  }

  // Find surrounding lengths for interpolation
  let lowerLength = lengths[0];
  let upperLength = lengths[lengths.length - 1];

  for (let i = 0; i < lengths.length - 1; i++) {
    if (lengths[i] <= length && lengths[i + 1] > length) {
      lowerLength = lengths[i];
      upperLength = lengths[i + 1];
      break;
    }
  }

  // If length is below minimum, use minimum
  if (length < lowerLength) {
    return sexData[lowerLength];
  }

  // If length is above maximum, use maximum
  if (length > upperLength) {
    return sexData[upperLength];
  }

  // Linear interpolation
  const lowerParams = sexData[lowerLength];
  const upperParams = sexData[upperLength];
  const fraction = (length - lowerLength) / (upperLength - lowerLength);

  return {
    L: lowerParams.L + (upperParams.L - lowerParams.L) * fraction,
    M: lowerParams.M + (upperParams.M - lowerParams.M) * fraction,
    S: lowerParams.S + (upperParams.S - lowerParams.S) * fraction,
  };
}

/**
 * Converts a Z-score to percentile using the cumulative distribution function
 */
function zScoreToPercentile(z: number): number {
  // Using the error function approximation for cumulative normal distribution
  const sign = z >= 0 ? 1 : -1;
  const x = Math.abs(z) / Math.sqrt(2);

  // Error function approximation
  const t = 1 / (1 + 0.3275911 * x);
  const erf =
    1 -
    ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-x * x);

  // Convert to percentile
  const percentile = 50 * (1 + sign * erf);

  // Clamp to valid range
  return Math.max(0, Math.min(100, percentile));
}

/**
 * Determines the nutritional status category based on WHO standards
 * Using Z-score thresholds:
 * - Severely wasted: < -3 SD
 * - Wasted: -3 to < -2 SD
 * - Normal: -2 to +2 SD
 * - Possible risk of overweight: +2 to < +3 SD
 * - Overweight/Obese: >= +3 SD
 */
function getCategory(
  zScore: number,
  percentile: number,
): {
  category: WeightForLengthResult['category'];
  categoryDescription: string;
  interpretation: string;
} {
  if (zScore < -3) {
    return {
      category: 'Severely wasted',
      categoryDescription: 'Severely wasted (Z-score < -3 SD)',
      interpretation:
        'The child is severely underweight for their length, indicating severe acute malnutrition. Immediate medical evaluation and nutritional intervention required.',
    };
  } else if (zScore < -2) {
    return {
      category: 'Wasted',
      categoryDescription: 'Wasted (Z-score -3 to -2 SD)',
      interpretation:
        'The child is underweight for their length, indicating acute malnutrition. Medical evaluation and nutritional support recommended.',
    };
  } else if (zScore <= 2) {
    return {
      category: 'Normal',
      categoryDescription: 'Normal weight-for-length (Z-score -2 to +2 SD)',
      interpretation:
        'The child has normal weight for their length. Continue regular growth monitoring and maintain healthy feeding practices.',
    };
  } else if (zScore <= 3) {
    return {
      category: 'Possible risk of overweight',
      categoryDescription: 'Possible risk of overweight (Z-score +2 to +3 SD)',
      interpretation:
        'The child is above normal weight for their length, indicating possible risk of overweight. Consider dietary assessment and promote healthy eating habits.',
    };
  } else {
    return {
      category: 'Overweight',
      categoryDescription: 'Overweight (Z-score > +3 SD)',
      interpretation:
        'The child is significantly overweight for their length. Medical evaluation recommended to assess for underlying conditions and provide appropriate nutritional guidance.',
    };
  }
}

/**
 * Helper function to get severity type for UI color coding
 */
export function getWeightForLengthSeverity(
  category: WeightForLengthResult['category'],
): 'error' | 'warn' | 'success' | 'info' {
  switch (category) {
    case 'Severely wasted':
    case 'Overweight':
    case 'Obese':
      return 'error';
    case 'Wasted':
    case 'Possible risk of overweight':
      return 'warn';
    case 'Normal':
      return 'success';
    default:
      return 'info';
  }
}

/**
 * Provides reference ranges for display
 */
export function getWeightForLengthReferenceRanges(): Array<{
  zScore: string;
  category: string;
  description: string;
}> {
  return [
    {
      zScore: '< -3 SD',
      category: 'Severely wasted',
      description: 'Severe acute malnutrition',
    },
    {
      zScore: '-3 to -2 SD',
      category: 'Wasted',
      description: 'Acute malnutrition',
    },
    {
      zScore: '-2 to +2 SD',
      category: 'Normal',
      description: 'Healthy weight-for-length',
    },
    {
      zScore: '+2 to +3 SD',
      category: 'Possible risk of overweight',
      description: 'Above normal, monitor closely',
    },
    {
      zScore: '> +3 SD',
      category: 'Overweight',
      description: 'Significantly overweight',
    },
  ];
}
